import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const NOINDEX = "noindex, follow";

/**
 * 404 page.
 *
 * SEO: unknown URLs must never be indexed. Without this, the auto-SEO layer
 * would fall back to the homepage title/description and set a self-referencing
 * canonical on the junk URL, creating duplicate content and index bloat. Here
 * we force a `noindex` robots directive and drop the canonical while the page
 * is mounted, restoring the previous values on unmount.
 */
export default function NotFound() {
  const location = useLocation();

  useEffect(() => {
    const robots = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const prevRobots = robots ? robots.getAttribute("content") : null;
    let createdRobots = false;
    if (robots) {
      robots.setAttribute("content", NOINDEX);
    } else {
      const el = document.createElement("meta");
      el.setAttribute("name", "robots");
      el.setAttribute("content", NOINDEX);
      document.head.appendChild(el);
      createdRobots = true;
    }

    const canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute("href") : null;
    if (canonical) canonical.remove();

    return () => {
      if (createdRobots) {
        document.head.querySelector('meta[name="robots"]')?.remove();
      } else if (robots) {
        if (prevRobots === null) robots.removeAttribute("content");
        else robots.setAttribute("content", prevRobots);
      }

      if (prevCanonical !== null) {
        let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
        if (!link) {
          link = document.createElement("link");
          link.setAttribute("rel", "canonical");
          document.head.appendChild(link);
        }
        link.setAttribute("href", prevCanonical);
      }
    };
  }, [location.pathname]);

  return (
    <section className="relative flex min-h-[70vh] flex-col items-center justify-center overflow-hidden px-4 py-20 text-center">
      <span
        className="pointer-events-none select-none font-heading text-[7rem] font-black leading-none text-background-200 md:text-[11rem]"
        aria-hidden="true"
      >
        404
      </span>
      <h1 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
        Page not found
      </h1>
      <p className="mt-4 max-w-md text-sm leading-relaxed text-foreground-600 md:text-base">
        The page you are looking for does not exist or may have moved. Let us help you find the
        shifting service you need in Bangalore.
      </p>
      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <Link
          to="/"
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-home-4-line text-lg" aria-hidden="true" /> Back to Home
        </Link>
        <Link
          to="/contact"
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 px-6 py-3.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50"
        >
          <i className="ri-customer-service-2-line text-lg" aria-hidden="true" /> Contact Us
        </Link>
      </div>
    </section>
  );
}